import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { CreateTrustedAdvisorRequest } from '../../types/trustedAdvisor';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { Textarea } from '../common/Textarea';
import { Card } from '../common/Card';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Badge } from '../common/Badge';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  Users, 
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface TrustedAdvisorInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: () => void;
}

const TrustedAdvisorInvitationModal: React.FC<TrustedAdvisorInvitationModalProps> = ({
  isOpen,
  onClose,
  onInvite
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<CreateTrustedAdvisorRequest>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    relationship: '',
    invitation_message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const relationships = [
    'Spouse/Partner',
    'Parent',
    'Child',
    'Sibling',
    'Friend',
    'Attorney',
    'Financial Advisor',
    'Doctor/Healthcare Provider',
    'Executor',
    'Trustee',
    'Power of Attorney',
    'Other'
  ];

  const handleInputChange = (field: keyof CreateTrustedAdvisorRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !formData.relationship) {
        throw new Error('Please fill in all required fields');
      }

      // Create the trusted advisor
      await TrustedAdvisorService.createTrustedAdvisor(formData);
      
      setSuccess(true);
      setTimeout(() => {
        onInvite();
        onClose();
        setSuccess(false);
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          relationship: '',
          invitation_message: ''
        });
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setSuccess(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        relationship: '',
        invitation_message: ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Invite Trusted Advisor
                </h2>
                <p className="text-sm text-gray-600">
                  Send an invitation to someone you trust
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Invitation Sent Successfully!
              </h3>
              <p className="text-gray-600">
                Your trusted advisor invitation has been sent to {formData.email}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="Enter first name"
                    required
                    disabled={loading}
                    icon={<User className="w-4 h-4" />}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Enter last name"
                    required
                    disabled={loading}
                    icon={<User className="w-4 h-4" />}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    required
                    disabled={loading}
                    icon={<Mail className="w-4 h-4" />}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    disabled={loading}
                    icon={<Phone className="w-4 h-4" />}
                  />
                </div>
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship *
                </label>
                <Select
                  value={formData.relationship}
                  onChange={(value) => handleInputChange('relationship', value)}
                  options={relationships.map(rel => ({ value: rel, label: rel }))}
                  placeholder="Select relationship"
                  disabled={loading}
                />
              </div>

              {/* Invitation Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personal Message
                </label>
                <Textarea
                  value={formData.invitation_message}
                  onChange={(e) => handleInputChange('invitation_message', e.target.value)}
                  placeholder="Add a personal message to your invitation (optional)"
                  rows={4}
                  disabled={loading}
                  icon={<MessageSquare className="w-4 h-4" />}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This message will be included in the invitation email
                </p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Invitation</span>
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TrustedAdvisorInvitationModal; 